import { Form, Input, Select, Modal, Radio } from 'antd';
import FileUpload from 'components/FileUpload';
import OmniNotification from 'components/OmniNotification';
import { useForm, FormProvider } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
	ProjectBuildCommand,
	updateProjectBuildName,
	uploadProjectBuild,
} from 'redux/reducers/projectsSlice';
import { AppDispatch } from 'redux/store';
import {
	FileModel,
	FileYupSchema,
	getUploadedFilesByExt,
	GetUploadLink,
	ValidateProductBuild,
	ValidateS3Keys,
} from 'utils/fileUtils';
import FormItem from 'components/form/FormItem';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { focusFirstErrorField } from 'utils/validationUtils';
import { useMemo, useState } from 'react';
import api, {
	BuildUpdateType,
	ErrorCode,
	ErrorResult,
	ProductReleaseDto,
	ProductStatus,
	ReleaseChannel,
} from 'api';
import _ from 'lodash';
import styles from './Modal.module.less';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;
const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		buildName: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('projectsBuildPage.Inputs.Name'),
				}),
			)
			.max(
				100,
				t('validation.maxLength', {
					fieldName: t('projectsBuildPage.Inputs.Name'),
					count: 100,
				}),
			),
		updateType: yup.number().required(
			t('validation.required', {
				fieldName: t('projectsBuildPage.Inputs.UpdateType'),
			}),
		),
		required: yup.boolean().required(
			t('validation.required', {
				fieldName: t('projectsBuildPage.Inputs.Required'),
			}),
		),
		buildFiles: yup
			.array()
			.of(yup.object(FileYupSchema))
			.test('ValidateS3Keys', '', ValidateS3Keys),
	});
interface Props {
	visible: boolean;
	setVisible: (visible: boolean) => void;
	projectId: number;
	projectStatus: ProductStatus | undefined;
	afterSubmit: () => void;
	isUploading: (isLoading: boolean) => void;
	buildInfo?: ProductReleaseDto;
	existingBuilds: ProductReleaseDto[];
}
interface FormValues {
	buildName: string;
	updateType: BuildUpdateType;
	required: boolean;
	buildFiles: FileModel[];
	releaseChannel: any;
}
export default function BuildModal(props: Props) {
	const {
		visible,
		setVisible,
		projectId,
		projectStatus,
		afterSubmit,
		isUploading,
		buildInfo,
		existingBuilds,
	} = props;
	const { t } = useTranslation();
	const showUpdateType = useMemo(() => {
		const projectApproved =
			!!projectStatus &&
			[ProductStatus.Released, ProductStatus.ApprovedForStore].includes(
				projectStatus,
			);

		const hasBuildsInStore = existingBuilds.some(
			(b) => b.releaseChannelId === ReleaseChannel.Store,
		);
		return projectApproved || hasBuildsInStore;
	}, [projectStatus, existingBuilds]);
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const [isLoading, setIsLoading] = useState(false);
	const methods = useForm<FormValues>({
		mode: 'all',
		defaultValues: {
			updateType: BuildUpdateType.Fix,
			required: !showUpdateType,
			buildFiles: [],
			releaseChannel: ReleaseChannel.BETA,
		},
		resolver: yupResolver(validationSchema),
	});
	const {
		control,
		handleSubmit,
		setError,
		trigger,
		setFocus,
		watch,
		formState,
		reset,
	} = methods;
	const buildFilesFormValue = watch('buildFiles');
	const buildName = watch('buildName');
	const dispatch: AppDispatch = useDispatch();
	const preValidateForm = async () => {
		const obbFiles = getUploadedFilesByExt(buildFilesFormValue, 'obb');
		const apkFiles = getUploadedFilesByExt(buildFilesFormValue, 'apk');

		const obbWithoutApk = obbFiles.length && !apkFiles.length;
		const multipleApk = apkFiles.length > 1;
		const missingApk = apkFiles.length <= 0;
		if (obbWithoutApk || multipleApk || missingApk) {
			await trigger(undefined);

			if (obbWithoutApk || missingApk) {
				setError('buildFiles', {
					message: t('addProductPage.ProductFile.validation.OneApk'),
				});
			}

			if (multipleApk) {
				setError('buildFiles', {
					message: t('addProductPage.ProductFile.validation.OnlyOneApk'),
				});
			}

			focusFirstErrorField({
				setFocus,
				errors: formState.errors,
				fields: Object.keys(validationSchema.fields),
			});

			return false;
		}

		return true;
	};
	const finishUpload = () => {
		isUploading(false);
		setIsLoading(false);
		setVisible(false);
		reset();
		afterSubmit();
	};
	const submitBuild = () => {
		handleSubmit((values: FormValues) => {
			const doUpdate = async () => {
				const data: ProjectBuildCommand = {
					projectId: projectId,
					buildName: values.buildName,
					updateType: values.updateType,
					required: values.required,
					releaseChannelId: values.releaseChannel,
					buildDetails: values.buildFiles,
				};
				isUploading(true);
				setIsLoading(true);
				try {
					await api.uploadProjectBuild(data.projectId, data);
					await new Promise((resolve) => setTimeout(resolve, 4000));
					OmniNotification.success({
						message: t('projectBuildsPage.UploadSuccess.Message'),
						description: t('projectBuildsPage.UploadSuccess.Description', {
							channel: ReleaseChannel[values.releaseChannel],
						}),
					});
					finishUpload();
				} catch (error: any) {
					if (
						(error as ErrorResult).code ===
						ErrorCode.ProductBuild_PackageNameExists
					) {
						OmniNotification.info({
							message: t('projectBuildsPage.UploadFailed.Message'),
							description: error.message || t('api.defaultError'),
						});
					} else {
						OmniNotification.error({
							message: t('notification.error.title'),
							description: error.message || t('api.defaultError'),
						});
					}

					isUploading(false);
					setIsLoading(false);
				}
			};

			if (
				existingBuilds.find((b) => b.releaseChannelId === values.releaseChannel)
			) {
				openReplaceConfirmation(doUpdate);
			} else {
				doUpdate();
			}
		})();
	};
	const handleFinish = async () => {
		if (!buildInfo) {
			const isValid = await preValidateForm();
			isValid && handleSubmit(submitBuild)();
		} else {
			const isNameValid = await trigger('buildName', { shouldFocus: true });
			if (!isNameValid) return;
			if (_.isNil(buildInfo.releaseChannelId)) return;
			dispatch(
				updateProjectBuildName({
					projectId,
					buildName: methods.getValues('buildName'),
					releaseChannelId: buildInfo.releaseChannelId,
				}),
			).then((result) => {
				OmniNotification.success({
					message: t('projectBuildsPage.Actions.Edit.Message'),
					description: t('projectBuildsPage.Actions.Edit.Description', {
						buildName: methods.getValues('buildName'),
					}),
				});
				finishUpload();
			});
		}
	};

	const openReplaceConfirmation = (doUpdate: () => void) => {
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('buildDetailsPage.ReplacingConfirmation.Title')}
				</span>
			),
			okText: t('buildDetailsPage.ReplacingConfirmation.OkButton'),
			cancelText: t('buildDetailsPage.ReplacingConfirmation.CancelButton'),
			onOk() {
				doUpdate();
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: <span>{t('buildDetailsPage.ReplacingConfirmation.Body')}</span>,
		});
	};
	return (
		<Modal
			visible={visible}
			onCancel={() => {
				reset();
				setVisible(false);
			}}
			title={t('projectBuildsPage.UploadBuild')}
			onOk={handleFinish}
			destroyOnClose={true}
			okButtonProps={{
				loading: isLoading,
				disabled:
					buildName == buildInfo?.buildName ||
					!buildName ||
					(!buildInfo && !buildFilesFormValue.length),
			}}
			cancelButtonProps={{ className: 'color-dark', disabled: isLoading }}
			okText={buildInfo ? t('common.SaveChanges') : t('common.Upload')}
			maskClosable={false}
			keyboard={false}
		>
			<FormProvider {...methods}>
				<Form data-testid="form" layout="vertical">
					<FormItem
						name="buildName"
						label={t('projectsBuildPage.Inputs.Name')}
						control={control}
						render={({ field }) => (
							<Input {...field} defaultValue={buildInfo?.buildName} />
						)}
					/>
					{!buildInfo && (
						<>
							{showUpdateType && (
								<FormItem
									name="updateType"
									label={t('projectsBuildPage.Inputs.UpdateType')}
									control={control}
									render={({ field }) => (
										<Radio.Group {...field}>
											<Radio key="fix" value={BuildUpdateType.Fix}>
												{t('projectsBuildPage.Inputs.UpdateType.Fix')}
											</Radio>
											<Radio key="fix" value={BuildUpdateType.Feature}>
												{t('projectsBuildPage.Inputs.UpdateType.Feature')}
											</Radio>
										</Radio.Group>
									)}
								/>
							)}
							{showUpdateType && (
								<FormItem
									name="required"
									label={t('projectsBuildPage.Inputs.Required.Hint')}
									control={control}
									render={({ field }) => (
										<Radio.Group {...field}>
											<Radio key="required" value={true}>
												{t('projectsBuildPage.Inputs.Required.Required')}
											</Radio>
											<Radio key="optional" value={false}>
												{t('projectsBuildPage.Inputs.Required.Optional')}
											</Radio>
										</Radio.Group>
									)}
								/>
							)}
							<FormItem
								name="releaseChannel"
								label={t('projectsBuildPage.Inputs.Channel')}
								control={control}
								render={({ field }) => (
									<Select {...field}>
										<Option value={ReleaseChannel.BETA}>BETA</Option>
										<Option value={ReleaseChannel.RC}>RC</Option>
									</Select>
								)}
							/>
							<FormItem
								label={t('projectsBuildPage.Inputs.Files')}
								name="buildFiles"
								control={control}
								hideError={true}
								render={({ field, fieldState }) => (
									<FileUpload
										error={fieldState.error}
										hint={t('addProductPage.ProductFile.limit')}
										defaultFileList={[]}
										maxCount={256}
										multiple
										uploadLinkFunction={GetUploadLink}
										field={field as any}
										strictMaxCount
										allowedItemList={['apk']}
										disallowedItemsHint={t('addProductPage.ProductFile.limit')}
										postUploadValidation={{
											api: ValidateProductBuild,
											errorMessage: t('uploader.fileTypeError'),
										}}
									/>
								)}
							/>
						</>
					)}
				</Form>
			</FormProvider>
		</Modal>
	);
}
